/**
 * W1-11b (app-shell-ui): the command palette (cmdk; DESIGN.md §4.19, §8.4).
 *
 *  - No prefix (or `>`): commands. Fuzzy search over titles, aliases (RPE and Chinese community terms), ids and
 *    categories; recent commands first; each row shows the icon, the title, a "Full" tag for Full-level commands at
 *    the Guided level, the category or the reason it is disabled now, and the first shortcut. Disabled commands stay
 *    visible with their reason; commands that do not apply here appear only when searched, in their own group.
 *  - `#` lines, `@` time and markers, `?` help, `:` settings, `=` queries: the registered `PaletteProvider`s of that
 *    prefix (the shell provides `@`); results may arrive asynchronously (stale answers are dropped).
 *  - Parameterised commands prompt inline after they are picked (number, beat with live echo, line, text, choice),
 *    e.g. "Set beat snap" then `12` or `1/12`. Backspace on an empty prompt or Esc goes back to the list.
 *  - Running closes the palette first (focus goes back to the editing region), then runs with source 'palette', so
 *    Full-level commands in Guided ask "Run once" / "Switch to Full" (registry).
 *  - W3-01 (UX-009): one row per action (same-title commands of two modules collapse to the one that works here),
 *    runnable commands before disabled ones, "..." only when the title has no ellipsis yet, extra community aliases
 *    (paletteSearch.ts EXTRA_ALIASES / HELP_ALIASES; a strong glossary match like "cover" rides with the results),
 *    and Enter on a query with no result selects it, so typing starts over.
 */

import * as RD from '@radix-ui/react-dialog';
import { Command } from 'cmdk';
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { Json, MessageRef } from '../../core/util/types';
import type { LineId } from '../../core/model/types';
import { formatBeat, parseBeat } from '../../core/time/beatSyntax';
import { Badge } from '../../ui/Badge';
import { Icon } from '../../ui/Icon';
import { Kbd } from '../../ui/Kbd';
import type { AppServices, ArgSpec, CommandDef, PaletteItem, PaletteProvider } from '../registry/types';
import type { PalettePrefix, ShellUiService, UiSnapshot } from '../uiService';
import { chordsFor, chordsForArg, commandState, type CommandState } from './commandState';
import { endsWithEllipsis, EXTRA_ALIASES, HELP_ALIASES, rank, searchScore } from './paletteSearch';
import { beatArg, playheadSec, tempoOf } from './playback';
import { useShell, useSettingsValue, useText, useUi } from './ShellContext';
import { m } from './shell.messages';
import p from './Palette.module.css';

/** settings ext key: recently run palette commands, most recent first. */
export const RECENT_EXT = 'shell.recentCommands';
const MAX_RECENT = 12;
const PROVIDER_PREFIXES: readonly Exclude<PalettePrefix, '>'>[] = ['#', '@', '?', ':', '='];

const PLACEHOLDERS: Readonly<Record<PalettePrefix, typeof m.palettePlaceholder>> = {
  '>': m.palettePlaceholder,
  '#': m.palettePlaceholderLines,
  '@': m.palettePlaceholderTime,
  '?': m.palettePlaceholderHelp,
  ':': m.palettePlaceholderSettings,
  '=': m.palettePlaceholderQuery,
};

const PREFIX_NAMES: Readonly<Record<PalettePrefix, typeof m.prefixCommands>> = {
  '>': m.prefixCommands,
  '#': m.prefixLines,
  '@': m.prefixTime,
  '?': m.prefixHelp,
  ':': m.prefixSettings,
  '=': m.prefixQuery,
};

/** Splits the input into its prefix and query ("@12:1/4" -> ['@', '12:1/4']; "add line" -> ['>', 'add line']). */
export function splitInput(input: string): readonly [PalettePrefix, string] {
  const first = input.charAt(0);
  if (first === '>') return ['>', input.slice(1).trimStart()];
  if ((PROVIDER_PREFIXES as readonly string[]).includes(first))
    return [first as PalettePrefix, input.slice(1).trimStart()];
  return ['>', input];
}

function inputFor(prefix: PalettePrefix): string {
  return prefix === '>' ? '' : prefix;
}

/** Parses a number typed for a numeric argument ("1/12" means 12 for divisor-style arguments starting at 1). */
export function parseNumberArg(text: string, spec: Extract<ArgSpec, { kind: 'number' }>): number | null {
  const t = text.trim();
  if (t === '') return null;
  let v: number;
  const frac = /^(-?\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/.exec(t);
  if (frac !== null) {
    const a = Number(frac[1]);
    const b = Number(frac[2]);
    v = a === 1 && (spec.min ?? 0) >= 1 ? b : b === 0 ? NaN : a / b;
  } else {
    v = Number(t);
  }
  if (!Number.isFinite(v)) return null;
  if (spec.min !== undefined && v < spec.min) return null;
  if (spec.max !== undefined && v > spec.max) return null;
  return v;
}

const selectPalette = (snap: UiSnapshot): UiSnapshot['palette'] => snap.palette;

type Mode = { readonly kind: 'search' } | { readonly kind: 'args'; readonly cmd: CommandDef; readonly spec: ArgSpec };

export function Palette(props: { readonly services: AppServices; readonly ui: ShellUiService }) {
  const { services, ui } = props;
  const env = useShell();
  const palette = useUi(ui, selectPalette);
  const text = useText(services);
  const settings = useSettingsValue(services);
  const [input, setInput] = useState('');
  const [mode, setMode] = useState<Mode>({ kind: 'search' });
  const [selected, setSelected] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const opener = useRef<Element | null>(null);
  const [nonce, setNonce] = useState(palette.nonce);

  // A new openPalette(prefix): reset the input to that prefix.
  if (palette.open && palette.nonce !== nonce) {
    setNonce(palette.nonce);
    setInput(inputFor(palette.prefix));
    setMode({ kind: 'search' });
    setSelected('');
  }

  const { setAtom } = env;
  useEffect(() => {
    setAtom('paletteOpen', palette.open);
  }, [palette.open, setAtom]);

  const close = useCallback((): void => {
    ui.closePalette();
  }, [ui]);

  /** Focus back where it was before the palette (else the last editing region). */
  const restoreFocus = useCallback((): void => {
    const back = opener.current;
    opener.current = null;
    if (back instanceof HTMLElement && back.isConnected && back !== document.body) back.focus({ preventScroll: true });
    else env.focus?.handBack();
  }, [env.focus]);

  /** Close (focus goes back to the editing region), then run: commands act there, not on the palette. */
  const afterClose = useCallback(
    (fn: () => void): void => {
      close();
      setTimeout(fn, 0);
    },
    [close],
  );

  const execute = useCallback(
    (id: string, args?: Json): void => {
      const recent = (settings.ext[RECENT_EXT] as readonly Json[] | undefined) ?? [];
      const next = [id, ...recent.filter((x): x is string => typeof x === 'string' && x !== id)].slice(0, MAX_RECENT);
      services.settings.setExt(RECENT_EXT, next);
      afterClose(() => {
        void services.registry.runAny(id, args, 'palette');
      });
    },
    [afterClose, services, settings.ext],
  );

  const [prefix, query] = mode.kind === 'search' ? splitInput(input) : (['>', input] as const);

  const back = (): void => {
    setMode({ kind: 'search' });
    setInput('');
    setSelected('');
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>): void => {
    if (mode.kind === 'args' && e.key === 'Backspace' && input === '') {
      e.preventDefault();
      back();
      return;
    }
    // Enter with nothing to run (an invalid time, no match): select the query, so typing replaces it (UX-009).
    if (e.key === 'Enter' && input !== '') {
      const root = e.currentTarget.closest('[cmdk-root]');
      const runnable = root?.querySelector('[cmdk-item]:not([data-disabled="true"])') ?? null;
      if (runnable === null) {
        e.preventDefault();
        e.currentTarget.select();
      }
    }
  };

  const placeholder = mode.kind === 'args' ? text.tr(mode.spec.label) : text.t(PLACEHOLDERS[prefix]);
  const providers = useMemo(
    () => (palette.open ? services.registry.paletteProviders(services) : []),
    [palette.open, services],
  );
  const prefixProviders = useMemo(() => providers.filter((x) => x.prefix === prefix), [providers, prefix]);
  const availablePrefixes = useMemo<readonly PalettePrefix[]>(
    () => ['>', ...PROVIDER_PREFIXES.filter((x) => providers.some((pr) => pr.prefix === x))],
    [providers],
  );

  return (
    <RD.Root
      open={palette.open}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <RD.Portal>
        <RD.Overlay className={p.overlay} />
        <RD.Content
          className={p.dialog}
          aria-describedby={undefined}
          onOpenAutoFocus={() => {
            opener.current = document.activeElement;
          }}
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            restoreFocus();
          }}
          onEscapeKeyDown={(e) => {
            if (mode.kind !== 'args') return;
            e.preventDefault();
            back();
          }}
        >
          <RD.Title className={p.srOnly}>{text.t(m.paletteLabel)}</RD.Title>
          <Command
            className={p.root}
            label={text.t(m.paletteLabel)}
            shouldFilter={false}
            loop
            value={selected}
            onValueChange={setSelected}
          >
            {mode.kind === 'args' && (
              <div className={p.argHeader}>
                <button type="button" className={p.argBack} onClick={back} aria-label={text.t(m.paletteArgBack)}>
                  <Icon name="ArrowLeft" size={14} />
                </button>
                {mode.cmd.icon !== undefined && <Icon name={mode.cmd.icon} size={14} />}
                <span className={p.argTitle}>{text.t(mode.cmd.title)}</span>
                <span className={p.argHint}>{text.t(m.paletteArgHint)}</span>
              </div>
            )}
            <div className={p.inputRow}>
              <Icon name={mode.kind === 'args' ? 'CornerDownRight' : 'Search'} size={16} />
              <Command.Input
                ref={inputRef}
                className={p.input}
                value={input}
                onValueChange={setInput}
                placeholder={placeholder}
                onKeyDown={onKeyDown}
              />
            </div>
            <Command.List className={p.list}>
              {mode.kind === 'args' ? (
                <ArgItems
                  services={services}
                  cmd={mode.cmd}
                  spec={mode.spec}
                  query={input}
                  onRun={(args) => {
                    execute(mode.cmd.id, args);
                  }}
                />
              ) : prefix === '>' ? (
                <CommandItems
                  services={services}
                  query={query}
                  recent={settings.ext[RECENT_EXT]}
                  guided={settings.general.level === 'guided'}
                  onPick={(cmd) => {
                    if (cmd.args !== undefined) {
                      setMode({ kind: 'args', cmd, spec: cmd.args });
                      setInput('');
                      setSelected('');
                      inputRef.current?.focus();
                      return;
                    }
                    execute(cmd.id);
                  }}
                  onRunItem={(item) => {
                    afterClose(() => {
                      void Promise.resolve(item.run()).catch((err: unknown) => {
                        console.error('palette: provider item failed', err);
                      });
                    });
                  }}
                />
              ) : (
                <ProviderItems
                  services={services}
                  providers={prefixProviders}
                  prefix={prefix}
                  query={query}
                  onRun={(item) => {
                    afterClose(() => {
                      void Promise.resolve(item.run()).catch((err: unknown) => {
                        console.error('palette: provider item failed', err);
                      });
                    });
                  }}
                />
              )}
            </Command.List>
            {mode.kind === 'search' && (
              <div className={p.footer} aria-label={text.t(m.palettePrefixes)} role="group">
                {availablePrefixes.map((x) => (
                  <button
                    key={x}
                    type="button"
                    className={p.prefixChip}
                    data-active={x === prefix ? 'true' : undefined}
                    onMouseDown={(e) => {
                      e.preventDefault();
                    }}
                    onClick={() => {
                      setInput(inputFor(x));
                      inputRef.current?.focus();
                    }}
                  >
                    <kbd>{x}</kbd> {text.t(PREFIX_NAMES[x])}
                  </button>
                ))}
              </div>
            )}
          </Command>
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Commands
// ---------------------------------------------------------------------------------------------------------------

interface Row {
  readonly cmd: CommandDef;
  readonly state: CommandState;
}

const STATE_ORDER: Readonly<Record<CommandState['kind'], number>> = { enabled: 0, disabled: 1, hidden: 2 };

/**
 * One row per action (UX-009): commands of the same title and category registered by two modules (the motion
 * columns' and the curve editor's "Reverse segments", the Lines panel's and the preview's "Set parent...") show only
 * the ones that work best here (enabled before disabled before not applicable); two that both run here stay.
 */
export function dedupeRows<R extends { readonly cmd: CommandDef; readonly state: CommandState }>(
  rows: readonly R[],
  title: (cmd: CommandDef) => string,
): R[] {
  const best = new Map<string, number>();
  const keyOf = (r: R): string => `${title(r.cmd)}\u0000${r.cmd.category}`;
  for (const r of rows) {
    const k = keyOf(r);
    best.set(k, Math.min(best.get(k) ?? 9, STATE_ORDER[r.state.kind]));
  }
  return rows.filter((r) => STATE_ORDER[r.state.kind] === best.get(keyOf(r)));
}

/** Enabled rows first, each part in its order. */
export function enabledFirst<R extends { readonly state: CommandState }>(rows: readonly R[]): R[] {
  return [...rows.filter((r) => r.state.kind === 'enabled'), ...rows.filter((r) => r.state.kind !== 'enabled')];
}

/** A command's palette title: "{title}..." when it asks for an argument, unless the title already ends with one. */
export function paletteTitle(title: string, hasArgs: boolean, withArgs: (title: string) => string): string {
  return hasArgs && !endsWithEllipsis(title) ? withArgs(title) : title;
}

function CommandItems(props: {
  readonly services: AppServices;
  readonly query: string;
  readonly recent: Json | undefined;
  readonly guided: boolean;
  readonly onPick: (cmd: CommandDef) => void;
  readonly onRunItem: (item: PaletteItem) => void;
}) {
  const { services, query, guided } = props;
  const env = useShell();
  const text = useText(services);
  const recentIds = useMemo(
    () => (Array.isArray(props.recent) ? props.recent.filter((x): x is string => typeof x === 'string') : []),
    [props.recent],
  );
  const rows = useMemo<Row[]>(() => {
    const when = env.when();
    return dedupeRows(
      services.registry.commands().map((cmd) => ({ cmd, state: commandState(services, cmd.id, when, 'palette') })),
      (cmd) => text.t(cmd.title),
    );
  }, [env, services, text]);
  const fields = useCallback(
    (r: Row) => {
      const aliases = [...(r.cmd.aliases ?? []), ...(EXTRA_ALIASES[r.cmd.id] ?? [])];
      return {
        title: text.t(r.cmd.title),
        ...(aliases.length === 0 ? {} : { aliases }),
        category: text.t(m.category, { category: r.cmd.category }),
        id: r.cmd.id,
      };
    },
    [text],
  );

  const { recentRows, mainRows, otherRows } = useMemo(() => {
    const applicable = rows.filter((r) => r.state.kind !== 'hidden');
    if (query.trim() === '') {
      const byId = new Map(applicable.map((r) => [r.cmd.id, r]));
      const recentList = recentIds.map((id) => byId.get(id)).filter((r): r is Row => r !== undefined);
      const recentSet = new Set(recentList.map((r) => r.cmd.id));
      const main = enabledFirst(
        applicable
          .filter((r) => !recentSet.has(r.cmd.id))
          .sort(
            (a, b) =>
              a.cmd.category.localeCompare(b.cmd.category) || text.t(a.cmd.title).localeCompare(text.t(b.cmd.title)),
          ),
      );
      return { recentRows: recentList, mainRows: main, otherRows: [] as Row[] };
    }
    const recency = (r: Row): number => {
      const i = recentIds.indexOf(r.cmd.id);
      return i < 0 ? 0 : 60 - i * 4;
    };
    return {
      recentRows: [] as Row[],
      // Runnable commands first: a disabled one ("Cancel the paste") never tops the results.
      mainRows: enabledFirst(rank(applicable, query, fields, recency)),
      otherRows: rank(
        rows.filter((r) => r.state.kind === 'hidden' && services.registry.command(r.cmd.id) !== undefined),
        query,
        fields,
      ).slice(0, 8),
    };
  }, [rows, query, recentIds, fields, services, text]);

  // Help topics and glossary words under the commands (UX-009): "iscover" / "遮罩" find the community name
  // ("Hide notes under the line", RPE: Cover (isCover)) even though no command is named that yet.
  const helpSearch = useMemo(() => {
    const sync: PaletteItem[] = [];
    const pending: Array<Promise<readonly PaletteItem[]>> = [];
    if (query.trim() !== '') {
      for (const provider of services.registry.paletteProviders(services)) {
        if (provider.prefix !== '?') continue;
        try {
          const r = provider.search(query);
          if (Array.isArray(r)) sync.push(...(r as readonly PaletteItem[]));
          else pending.push(Promise.resolve(r));
        } catch (err) {
          console.error('palette: help provider failed', err);
        }
      }
    }
    return { query, sync, pending };
  }, [services, query]);
  const [helpLate, setHelpLate] = useState<{
    readonly of: typeof helpSearch;
    readonly items: readonly PaletteItem[];
  } | null>(null);
  useEffect(() => {
    if (helpSearch.pending.length === 0) return undefined;
    let alive = true;
    void Promise.allSettled(helpSearch.pending).then((all) => {
      if (!alive) return;
      setHelpLate({ of: helpSearch, items: all.flatMap((x) => (x.status === 'fulfilled' ? x.value : [])) });
    });
    return () => {
      alive = false;
    };
  }, [helpSearch]);
  // Help rows, best match first (UX-009): a strong community-name match ("cover" -> the glossary's
  // "Hide notes under the line", RPE: Cover (isCover) · 遮罩) rides with the results instead of under them.
  const allHelp: readonly PaletteItem[] =
    helpLate?.of === helpSearch ? [...helpSearch.sync, ...helpLate.items] : helpSearch.sync;
  const scoredHelp = allHelp
    .map((it) => ({
      it,
      score:
        query.trim() === ''
          ? 0
          : (searchScore(query, {
              title: text.tr(it.title),
              ...(it.detail === undefined ? {} : { detail: text.tr(it.detail) }),
              aliases: HELP_ALIASES[it.id] ?? [],
            }) ?? -1),
    }))
    .sort((a, b) => b.score - a.score);
  // Word-start matches and better (the score a substring at a word start earns): with the results, not under them.
  const strongHelp = scoredHelp.filter((x) => x.score >= 700).map((x) => x.it);
  const helpItems: PaletteItem[] = scoredHelp
    .filter((x) => x.score < 700)
    .slice(0, 4)
    .map((x) => x.it);

  if (recentRows.length === 0 && mainRows.length === 0 && otherRows.length === 0 && strongHelp.length === 0 && helpItems.length === 0) {
    return <Command.Empty className={p.empty}>{text.t(m.paletteEmpty)}</Command.Empty>;
  }
  const item = (r: Row, group: string) => (
    <CommandRow
      key={`${group}:${r.cmd.id}`}
      row={r}
      services={services}
      guided={guided}
      group={group}
      onPick={props.onPick}
    />
  );
  const helpRow = (it: PaletteItem, group: string, i: number) => (
    <Command.Item
      key={`${group}:${String(i)}:${it.id}`}
      value={`${group}:${it.id}`}
      className={p.item}
      data-help={it.id}
      onSelect={() => {
        props.onRunItem(it);
      }}
    >
      <span className={p.itemIcon}>{it.icon !== undefined && <Icon name={it.icon} size={16} />}</span>
      <span className={p.itemText}>
        <span className={p.itemTitle}>{text.tr(it.title)}</span>
      </span>
      {it.detail !== undefined && <span className={p.itemMeta}>{text.tr(it.detail)}</span>}
    </Command.Item>
  );
  return (
    <>
      {recentRows.length > 0 && (
        <Command.Group heading={text.t(m.paletteRecent)} className={p.group}>
          {recentRows.map((r) => item(r, 'recent'))}
        </Command.Group>
      )}
      {(strongHelp.length > 0 || mainRows.length > 0) && (
        <Command.Group heading={text.t(query.trim() === '' ? m.paletteCommands : m.paletteResults)} className={p.group}>
          {strongHelp.map((it, i) => helpRow(it, 'helpStrong', i))}
          {mainRows.map((r) => item(r, 'main'))}
        </Command.Group>
      )}
      {otherRows.length > 0 && (
        <Command.Group heading={text.t(m.paletteNotHereGroup)} className={p.group}>
          {otherRows.map((r) => item(r, 'other'))}
        </Command.Group>
      )}
      {helpItems.length > 0 && (
        <Command.Group heading={text.t(m.category, { category: 'Help' })} className={p.group}>
          {helpItems.map((it, i) => helpRow(it, 'help', i))}
        </Command.Group>
      )}
    </>
  );
}

function CommandRow(props: {
  readonly row: Row;
  readonly services: AppServices;
  readonly guided: boolean;
  readonly group: string;
  readonly onPick: (cmd: CommandDef) => void;
}) {
  const { row, services } = props;
  const text = useText(services);
  const { cmd, state } = row;
  // Only a binding without arguments runs the command as this row does (Shift+1 sets 1/1 and is listed with that
  // option in the argument step instead).
  const chord = chordsFor(services.keymap.bindings(), cmd.id)[0];
  const disabled = state.kind !== 'enabled';
  const reason: MessageRef | null = state.kind === 'enabled' ? null : state.reason;
  return (
    <Command.Item
      value={`${props.group}:${cmd.id}`}
      disabled={disabled}
      className={p.item}
      data-command={cmd.id}
      onSelect={() => {
        props.onPick(cmd);
      }}
    >
      <span className={p.itemIcon}>{cmd.icon !== undefined && <Icon name={cmd.icon} size={16} />}</span>
      <span className={p.itemText}>
        <span className={p.itemTitle}>
          {paletteTitle(text.t(cmd.title), cmd.args !== undefined, (title) => text.t(m.withArgs, { title }))}
        </span>
        {props.guided && cmd.level === 'full' && (
          <Badge kind="full" label={{ key: m.paletteFullTag }} tooltip={{ key: m.paletteFullTagHint }} />
        )}
      </span>
      <span className={reason === null ? p.itemMeta : p.itemReason}>
        {reason === null ? text.t(m.category, { category: cmd.category }) : text.tr(reason)}
      </span>
      {chord !== undefined && <Kbd chord={chord} className={p.itemKbd} />}
    </Command.Item>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Arguments
// ---------------------------------------------------------------------------------------------------------------

/** The shortcut that runs the command with this argument value, if any ("Shift+3" next to "1/3"). */
function ArgChord(props: { readonly services: AppServices; readonly cmd: CommandDef; readonly value: Json }) {
  const chord = chordsForArg(props.services.keymap.bindings(), props.cmd.id, props.value)[0];
  return chord === undefined ? null : <Kbd chord={chord} className={p.itemKbd} />;
}

function ArgItems(props: {
  readonly services: AppServices;
  readonly cmd: CommandDef;
  readonly spec: ArgSpec;
  readonly query: string;
  readonly onRun: (args: Json) => void;
}) {
  const { services, spec, query } = props;
  const text = useText(services);
  switch (spec.kind) {
    case 'choice': {
      const options = rank(spec.options, query, (o) => ({ title: text.tr(o.label) }));
      if (options.length === 0) return <Command.Empty className={p.empty}>{text.t(m.paletteEmpty)}</Command.Empty>;
      return (
        <>
          {options.map((o, i) => (
            <Command.Item
              key={`${String(i)}:${JSON.stringify(o.value)}`}
              value={`choice:${JSON.stringify(o.value)}`}
              className={p.item}
              onSelect={() => {
                props.onRun(o.value);
              }}
            >
              <span className={p.itemIcon} />
              <span className={p.itemText}>
                <span className={p.itemTitle}>{text.tr(o.label)}</span>
              </span>
              <ArgChord services={services} cmd={props.cmd} value={o.value} />
            </Command.Item>
          ))}
        </>
      );
    }
    case 'number': {
      const v = parseNumberArg(query, spec);
      if (v === null) {
        return (
          <div className={p.argMessage} role="status">
            {spec.min !== undefined && spec.max !== undefined
              ? text.t(m.paletteArgNumber, { min: spec.min, max: spec.max })
              : text.t(m.paletteArgNumberAny)}
          </div>
        );
      }
      return (
        <Command.Item
          value={`number:${String(v)}`}
          className={p.item}
          onSelect={() => {
            props.onRun(v);
          }}
        >
          <span className={p.itemIcon}>
            <Icon name="CornerDownLeft" size={16} />
          </span>
          <span className={p.itemText}>
            <span className={p.itemTitle}>
              {text.t(m.labelValue, { label: text.t(props.cmd.title), value: String(v) })}
            </span>
          </span>
          <ArgChord services={services} cmd={props.cmd} value={v} />
        </Command.Item>
      );
    }
    case 'beat':
      return <BeatArgItem services={services} cmd={props.cmd} query={query} onRun={props.onRun} />;
    case 'line':
      return <LineArgItems services={services} query={query} onRun={props.onRun} />;
    case 'text': {
      if (query.trim() === '') return <div className={p.argMessage}>{text.tr(spec.label)}</div>;
      return (
        <Command.Item
          value={`text:${query}`}
          className={p.item}
          onSelect={() => {
            props.onRun(query);
          }}
        >
          <span className={p.itemIcon}>
            <Icon name="CornerDownLeft" size={16} />
          </span>
          <span className={p.itemText}>
            <span className={p.itemTitle}>
              {text.t(m.labelValue, { label: text.t(props.cmd.title), value: query })}
            </span>
          </span>
        </Command.Item>
      );
    }
  }
}

function BeatArgItem(props: {
  readonly services: AppServices;
  readonly cmd: CommandDef;
  readonly query: string;
  readonly onRun: (args: Json) => void;
}) {
  const { services, query } = props;
  const text = useText(services);
  const store = services.store;
  if (query.trim() === '') return <div className={p.argMessage}>{text.t(m.palettePlaceholderTime)}</div>;
  const tempo = store === null ? undefined : tempoOf(store);
  const result = parseBeat(query, {
    snapDiv: services.editor.getState().snap.div,
    syntax: services.settings.value.editing.beatSyntax,
    ...(tempo === undefined ? {} : { tempo, current: tempo.beatAt(playheadSec(services)) }),
  });
  if (!result.ok) {
    return (
      <div className={p.argMessage} role="status">
        {text.t(m.paletteArgBeatInvalid)}
      </div>
    );
  }
  return (
    <Command.Item
      value={`beat:${query}`}
      className={p.item}
      onSelect={() => {
        props.onRun(beatArg(result.beat));
      }}
    >
      <span className={p.itemIcon}>
        <Icon name="CornerDownLeft" size={16} />
      </span>
      <span className={p.itemText}>
        <span className={p.itemTitle}>
          {text.t(m.labelValue, { label: text.t(props.cmd.title), value: formatBeat(result.beat) })}
        </span>
      </span>
      <span className={p.itemMeta}>{text.t(m.paletteArgBeatEcho, { beat: result.echo.replace(/^=\s*/, '') })}</span>
    </Command.Item>
  );
}

function LineArgItems(props: {
  readonly services: AppServices;
  readonly query: string;
  readonly onRun: (args: Json) => void;
}) {
  const { services, query } = props;
  const text = useText(services);
  const store = services.store;
  const lines = useMemo(() => {
    if (store === null) return [];
    return store.doc.lineOrder.map((id: LineId, i) => ({ id, index: i, name: store.doc.lines.get(id)?.name ?? '' }));
  }, [store]);
  if (store === null) return <div className={p.argMessage}>{text.t(m.paletteArgNoLines)}</div>;
  const q = query.trim();
  const byNumber = /^\d+$/.test(q) ? lines.filter((l) => String(l.index) === q) : [];
  const matched = [
    ...byNumber,
    ...rank(lines, q, (l) => ({ title: l.name, id: String(l.index) })).filter((l) => !byNumber.includes(l)),
  ];
  if (matched.length === 0) return <Command.Empty className={p.empty}>{text.t(m.paletteEmpty)}</Command.Empty>;
  return (
    <>
      {matched.slice(0, 200).map((l) => (
        <Command.Item
          key={l.id}
          value={`line:${String(l.id)}`}
          className={p.item}
          onSelect={() => {
            props.onRun(l.id);
          }}
        >
          <span className={p.itemIcon}>
            <Icon name="Minus" size={16} />
          </span>
          <span className={p.itemText}>
            <span className={p.itemTitle}>
              {l.name === ''
                ? text.t(m.paletteArgUnnamedLine, { index: l.index })
                : text.t(m.paletteArgLine, { index: l.index, name: l.name })}
            </span>
          </span>
        </Command.Item>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Providers
// ---------------------------------------------------------------------------------------------------------------

function ProviderItems(props: {
  readonly services: AppServices;
  readonly providers: readonly PaletteProvider[];
  readonly prefix: PalettePrefix;
  readonly query: string;
  readonly onRun: (item: PaletteItem) => void;
}) {
  const { providers, prefix, query, services } = props;
  const text = useText(services);
  // Synchronous answers are part of the render; promises fill in later (a newer search wins).
  const search = useMemo(() => {
    const sync: PaletteItem[] = [];
    const pending: Array<Promise<readonly PaletteItem[]>> = [];
    for (const provider of providers) {
      try {
        const r = provider.search(query);
        if (Array.isArray(r)) sync.push(...(r as readonly PaletteItem[]));
        else pending.push(Promise.resolve(r));
      } catch (err) {
        console.error(`palette: provider "${provider.prefix}" failed`, err);
      }
    }
    return { prefix, query, sync, pending };
  }, [providers, prefix, query]);
  const [late, setLate] = useState<{ readonly of: typeof search; readonly items: readonly PaletteItem[] } | null>(null);
  useEffect(() => {
    if (search.pending.length === 0) return undefined;
    let alive = true;
    void Promise.allSettled(search.pending).then((all) => {
      if (!alive) return;
      setLate({ of: search, items: all.flatMap((x) => (x.status === 'fulfilled' ? x.value : [])) });
    });
    return () => {
      alive = false;
    };
  }, [search]);

  if (providers.length === 0) return <div className={p.empty}>{text.t(m.paletteNothingYet)}</div>;
  const arrived = late?.of === search ? late.items : null;
  const current = {
    loading: search.pending.length > 0 && arrived === null,
    items: arrived === null ? search.sync : [...search.sync, ...arrived],
  };
  return (
    <>
      {current.loading && <Command.Loading className={p.loading}>{text.t(m.paletteLoading)}</Command.Loading>}
      {!current.loading && current.items.length === 0 && <div className={p.empty}>{text.t(m.paletteEmpty)}</div>}
      {current.items.map((it, i) => (
        <Command.Item
          key={`${String(i)}:${it.id}`}
          value={`p:${String(i)}:${it.id}`}
          className={p.item}
          onSelect={() => {
            props.onRun(it);
          }}
        >
          <span className={p.itemIcon}>{it.icon !== undefined && <Icon name={it.icon} size={16} />}</span>
          <span className={p.itemText}>
            <span className={p.itemTitle}>{text.tr(it.title)}</span>
          </span>
          {it.detail !== undefined && <span className={p.itemMeta}>{text.tr(it.detail)}</span>}
        </Command.Item>
      ))}
    </>
  );
}
